import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import type { DataTableType } from '@components/DataTable/DataTable';
import ViewTab from '@components/ViewTab';

import { ThrottledLoader } from '@contexts/index';

import { MarginDiv } from '@layouts/Layouts/BigTitleLayout';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';

import LabResultCardView from './LabResultCardView';
import LabResultListView from './LabResultListView';

const messages = defineMessages({
  title: {
    id: 'labResultsMain.title',
    defaultMessage: 'Lab Results',
  },
  listView: {
    id: 'labResultsMain.listView',
    defaultMessage: 'List View',
  },
  cardView: {
    id: 'labResultsMain.cardView',
    defaultMessage: 'Card View',
  },
});

interface LabResultsProps {
  data: DiagnosticReportRecord[];
  loading: boolean;
}
const LabResults: React.FC<LabResultsProps> = ({ data, loading }) => {
  const { formatMessage } = useIntl();

  const [displayData, setDisplayData] =
    useState<DiagnosticReportRecord[]>(data);

  const tabs = [
    { id: 'list-view', value: formatMessage(messages.listView) },
    { id: 'card-view', value: formatMessage(messages.cardView) },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const tableData = useMemo(
    () =>
      data.map((d) => {
        return {
          id: d.id ?? '',
          type: d.code ?? '',
          date:
            activeTab.id === 'card-view' ? d.getTestDate() : d.getTestDateStr(),
          title: d.typeName ?? '-',
          results: d.results ?? [],
          viewed: d.viewedStatus,
          testName: d.reportTestName,
          curationStatus: d.curationStatus,
          patient: d.patient,
          recordSource: d.recordSource,
        };
      }),
    [activeTab.id, data]
  );

  const records = displayData.map<LabResultSectionRecord>((d) => {
    return {
      id: d.id ?? '',
      type: d.code ?? '',
      date: d.getTestDateStr(),
      title: d.typeName ?? '-',
      results: d.results ?? [],
      viewed: d.viewedStatus,
      testName: d.reportTestName,
      selfReported: d.selfReported,
      curationStatus: d.curationStatus,
      resultCount: d.resultCount,
      patient: d.patient ?? '',
      recordSource: d.recordSource,
    };
  });

  const filterData = useCallback(
    (filteredItems: Record<string, DataTableType>[]) => {
      const ids = filteredItems.map(({ id }) => id);
      setDisplayData(
        data.filter((d) => ids.find((id) => id === d.id) !== undefined)
      );
    },
    [data]
  );

  useEffect(() => {
    setDisplayData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  return (
    <ContentLoader loading={loading}>
      <MarginDiv>
        <ViewTab
          changeTab={setActiveTab}
          selected={activeTab}
          tabArray={tabs}
        />
      </MarginDiv>
      {activeTab.id === 'card-view' ? (
        <ThrottledLoader.Provider concurrency={5} cache>
          <LabResultCardView
            data={data}
            displayData={displayData}
            tableData={tableData}
            onChange={filterData}
            records={records}
          />
        </ThrottledLoader.Provider>
      ) : null}
      {activeTab.id === 'list-view' ? (
        <LabResultListView data={data} tableData={tableData} />
      ) : null}
    </ContentLoader>
  );
};

export default LabResults;
